<div class="cal-wrap" *ngIf="rows.length > 0; else empty" (click)="closeDetail()">

  <!-- Legend + sort + zoom controls -->
  <div class="cal-legend">
    <span *ngFor="let l of legend" class="leg-item">
      <span class="leg-dot" [style.background]="l.color"></span>{{ l.label }}
    </span>

    <div class="cal-sort-toggle">
      <span class="sort-label">Group by</span>
      <button class="sort-btn" [class.active]="sortBy === 'operator'" (click)="setSortBy('operator')">
        Operator
      </button>
      <button class="sort-btn" [class.active]="sortBy === 'region'" (click)="setSortBy('region')">
        Region
      </button>

      <span class="sort-label zoom-label">Zoom</span>
      <button class="sort-btn" [class.active]="zoomPreset === '30d'" (click)="setZoom('30d')">30d</button>
      <button class="sort-btn" [class.active]="zoomPreset === '60d'" (click)="setZoom('60d')">60d</button>
      <button class="sort-btn" [class.active]="zoomPreset === '90d'" (click)="setZoom('90d')">90d</button>
      <button class="sort-btn" [class.active]="zoomPreset === 'all'" (click)="setZoom('all')">All</button>

      <button class="sort-btn jump-today-btn" (click)="jumpToToday()" title="Scroll to today">
        &#8594; Today
      </button>

      <button class="sort-btn collapse-all-btn"
              (click)="allCollapsed ? expandAll() : collapseAll()"
              [title]="allCollapsed ? 'Expand all groups' : 'Collapse all groups'">
        {{ allCollapsed ? 'Expand all' : 'Collapse all' }}
      </button>
    </div>
  </div>

  <!-- Scrollable timeline -->
  <div class="cal-scroll" #calScroll (click)="closeDetail()">

    <!-- Sticky date header -->
    <div class="cal-header">
      <div class="col-operator">{{ groupLabel }}</div>
      <div class="col-track">
        <!-- Month boundary lines (with labels in header) -->
        <div class="month-line"
             *ngFor="let m of monthLines"
             [style.left.%]="m.leftPct">
          <span class="month-line-label">{{ m.label }}</span>
        </div>
        <span class="date-label" *ngFor="let h of dateHeaders" [style.left.%]="h.leftPct">
          {{ h.label }}
        </span>
        <!-- Today tick on header -->
        <span class="today-tick" [style.left.%]="todayPct" title="Today"></span>
      </div>
    </div>

    <!-- Rows -->
    <div class="cal-body">
      <div class="cal-row"
           *ngFor="let row of rows; trackBy: trackByRow"
           [class.cal-row--collapsed]="isCollapsed(row.label)"
           [style.height.px]="getRowHeight(row)">

        <!-- Row label (operator or region) with well count badge -->
        <div class="col-operator row-toggle"
             [title]="row.label"
             (click)="toggleCollapsed(row.label, $event)">
          <span class="row-chevron" [class.row-chevron--collapsed]="isCollapsed(row.label)">&#9662;</span>
          <span class="row-label-text">{{ row.label }}</span>
          <span class="row-count-badge">{{ row.wells.length }}</span>
        </div>

        <!-- Bar track -->
        <div class="col-track">
          <!-- Today line -->
          <div class="today-line" [style.left.%]="todayPct"></div>

          <!-- Month boundary lines (no label in body rows) -->
          <div class="month-line"
               *ngFor="let m of monthLines"
               [style.left.%]="m.leftPct">
          </div>

          <!-- Frac job bars -->
          <ng-container *ngIf="!isCollapsed(row.label)">
            <div class="bar"
                 *ngFor="let bar of row.wells; trackBy: trackByBar"
                 [style.left.%]="bar.leftPct"
                 [style.width.%]="bar.widthPct"
                 [style.top.px]="bar.stackRow * 26 + 4"
                 [style.background]="bar.color"
                 [class.bar--selected]="selectedBar?.model?.scheduleId === bar.model.scheduleId"
                 [title]="bar.model.wellName"
                 (click)="onBarClick(bar, $event); $event.stopPropagation()">
              <span class="bar-label">{{ bar.model.wellName }}</span>
            </div>
          </ng-container>

          <!-- Collapsed: render thin status pips so the row still conveys timing at a glance -->
          <ng-container *ngIf="isCollapsed(row.label)">
            <div class="pip"
                 *ngFor="let bar of row.wells; trackBy: trackByBar"
                 [style.left.%]="bar.leftPct"
                 [style.width.%]="bar.widthPct"
                 [style.background]="bar.color"
                 [title]="bar.model.wellName">
            </div>
          </ng-container>
        </div>

      </div>
    </div>

  </div>

  <!-- Floating detail panel -->
  <div class="detail-panel"
       *ngIf="selectedBar"
       [style.top]="detailPanelStyle.top"
       [style.left]="detailPanelStyle.left"
       (click)="$event.stopPropagation()">

    <div class="detail-header">
      <span class="detail-well-name">{{ selectedBar.model.wellName }}</span>
      <span class="detail-status-badge" [style.background]="selectedBar.color">
        {{ statusLabel(selectedBar.model) }}
      </span>
      <button class="detail-close" (click)="closeDetail()">&#x2715;</button>
    </div>

    <div class="detail-fields">
      <div class="detail-field">
        <span class="detail-label">Operator</span>
        <span class="detail-value">{{ selectedBar.model.companyName || '—' }}</span>
      </div>
      <div class="detail-field">
        <span class="detail-label">State</span>
        <span class="detail-value">{{ selectedBar.model.state || '—' }}</span>
      </div>
      <div class="detail-field">
        <span class="detail-label">County</span>
        <span class="detail-value">{{ selectedBar.model.county || '—' }}</span>
      </div>
      <div class="detail-field">
        <span class="detail-label">Region</span>
        <span class="detail-value">{{ selectedBar.model.region || '—' }}</span>
      </div>
      <div class="detail-field">
        <span class="detail-label">Start</span>
        <span class="detail-value">{{ fmtDate(selectedBar.model.fracStartDate) }}</span>
      </div>
      <div class="detail-field">
        <span class="detail-label">End</span>
        <span class="detail-value">{{ fmtDate(selectedBar.model.fracEndDate) }}</span>
      </div>
      <div class="detail-field">
        <span class="detail-label">Duration</span>
        <span class="detail-value">
          {{ selectedBar.model.duration != null ? selectedBar.model.duration + ' days' : '—' }}
        </span>
      </div>
      <div class="detail-field" *ngIf="selectedBar.model.targetFormation">
        <span class="detail-label">Formation</span>
        <span class="detail-value">{{ selectedBar.model.targetFormation }}</span>
      </div>
    </div>

  </div>

</div>

<ng-template #empty>
  <div class="cal-empty">
    No schedule data — adjust your filters and click <strong>Apply</strong>.
  </div>
</ng-template>
